import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import { ICoreErrorResponse } from "@/core/interfaces/coreResponse.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import notificationService from "@/core/services/notification.service";
import {
  IProfileState,
  IAuthProfile,
} from "@/core/interfaces/profile.interface";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StoreState } from "@/store/type";
import AuthService from "@/modules/Profile/services/auth.service";
import type {
  UpdateUserProfileDto,
  VerifyAuthenticatorDto,
  VerifyDisableTwoFactorOtpDto,
} from "@/modules/Profile/dtos/auth.dto";
import analyticsService from "@/analytic.service";
import { ANALYTICS_EVENTS } from "@/modules/Profile/constants/analyticsEvents";

function mapResponseToAuthProfile(
  data: Record<string, unknown> | null,
): IAuthProfile | null {
  if (!data || typeof data !== "object") return null;
  const pairsView = data.flight_search_pairs_view ?? false;
  return {
    contact_number:
      (data.contact_number as string) ?? (data.phone_number as string) ?? "",
    whatsapp_number: (data.whatsapp_number as string) ?? "",
    flight_search_pairs_view: !!pairsView,
    is_multi_factor_enabled: !!data.is_multi_factor_enabled,
  };
}

const actions: ActionTree<IProfileState, StoreState> = {
  async fetchAuthProfile(context: ActionContext<IProfileState, StoreState>) {
    try {
      context.commit("setProfileFetching", true);

      const authService = new AuthService();
      const response = await authService.getUserProfile();

      const rawData = response.data as Record<string, unknown> | undefined;
      const payload = rawData?.data != null ? rawData.data : rawData;

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        payload != null
      ) {
        const profile = mapResponseToAuthProfile(
          payload as Record<string, unknown>,
        );
        if (profile) {
          context.commit("setAuthProfile", profile);
          analyticsService.logActionEvent(
            ANALYTICS_EVENTS.FETCH_PREFERENCES,
            profile,
          );
        }
      } else {
        throw response;
      }
    } catch (err) {
      console.error("Error fetching auth profile:", err);
    } finally {
      context.commit("setProfileFetching", false);
    }
  },

  async updateAuthProfile(
    context: ActionContext<IProfileState, StoreState>,
    payload: UpdateUserProfileDto,
  ) {
    try {
      context.commit("setProfileUpdateLoading", true);

      const authService = new AuthService();
      const response = await authService.updateUserProfile(payload);
      const body = response.data as any;
      const businessStatus = body?.status;
      const twoFactorData = body?.data;

      if (response.success && response.status === StatusCodes.OK) {
        if (
          businessStatus === 1 &&
          twoFactorData?.qr_code &&
          twoFactorData?.multi_factor_token
        ) {
          context.commit("setTwoFactorSetup", {
            qr_code: twoFactorData.qr_code,
            multi_factor_token: twoFactorData.multi_factor_token,
          });
          context.commit("setAuthProfile", {
            contact_number:
              payload.contact_number ??
              context.state.authProfile?.contact_number,
            whatsapp_number:
              payload.whatsapp_number ??
              context.state.authProfile?.whatsapp_number,
            flight_search_pairs_view:
              payload.flight_search_pairs_view ??
              context.state.authProfile?.flight_search_pairs_view ??
              false,
            is_multi_factor_enabled:
              context.state.authProfile?.is_multi_factor_enabled ?? false,
          });
          notificationService.type = NOTIFICATION_TYPES.INFO;
          notificationService.description =
            body?.message || "Please scan the QR code to enable 2FA";
          notificationService.triggerNotification();
        } else {
          const isDisable2FA =
            payload.is_multi_factor_enabled === false &&
            (context.state.authProfile?.is_multi_factor_enabled ?? false);

          if (isDisable2FA) {
            context.commit("setTwoFactorSetup", null);
            context.commit("setAuthProfile", {
              contact_number:
                payload.contact_number ??
                context.state.authProfile?.contact_number,
              whatsapp_number:
                payload.whatsapp_number ??
                context.state.authProfile?.whatsapp_number,
              flight_search_pairs_view:
                payload.flight_search_pairs_view ??
                context.state.authProfile?.flight_search_pairs_view ??
                false,
              is_multi_factor_enabled: true,
            });
            notificationService.type = NOTIFICATION_TYPES.SUCCESS;
            notificationService.description =
              body?.message || "OTP sent to your email";
            notificationService.triggerNotification();
            return { ...body, requiresDisable2FAOtp: true };
          }

          context.commit("setTwoFactorSetup", null);
          context.commit("setAuthProfile", {
            contact_number:
              payload.contact_number ??
              context.state.authProfile?.contact_number,
            whatsapp_number:
              payload.whatsapp_number ??
              context.state.authProfile?.whatsapp_number,
            flight_search_pairs_view:
              payload.flight_search_pairs_view ??
              context.state.authProfile?.flight_search_pairs_view ??
              false,
            is_multi_factor_enabled:
              payload.is_multi_factor_enabled ??
              context.state.authProfile?.is_multi_factor_enabled ??
              false,
          });
          notificationService.type = NOTIFICATION_TYPES.SUCCESS;
          notificationService.description =
            body?.message || "Profile updated successfully";
          notificationService.triggerNotification();
          analyticsService.logActionEvent(
            ANALYTICS_EVENTS.UPDATE_PREFERENCES,
            payload,
          );
        }

        return body;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      return error;
    } finally {
      context.commit("setProfileUpdateLoading", false);
    }
  },

  async verifyDisableTwoFactorOtp(
    context: ActionContext<IProfileState, StoreState>,
    payload: { otp_code: string },
  ) {
    try {
      const authService = new AuthService();
      const body = {
        otp_code: payload.otp_code,
      };
      const response = await authService.verifyDisableTwoFactorOtp(body);
      const responseBody = response.data as any;
      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          responseBody?.message || "Multi-factor authentication disabled";
        notificationService.triggerNotification();
        await context.dispatch("fetchAuthProfile");
        return responseBody;
      }
      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      throw error;
    }
  },

  async verifyAuthenticator(
    context: ActionContext<IProfileState, StoreState>,
    payload: VerifyAuthenticatorDto,
  ) {
    try {
      context.commit("setVerifyAuthenticatorLoading", true);

      const authService = new AuthService();
      const response = await authService.verifyAuthenticator(payload);
      const body = response.data as any;

      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setTwoFactorSetup", null);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          body?.message || "Multi-factor authentication verified";
        notificationService.triggerNotification();
        await context.dispatch("fetchAuthProfile");
        return body;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      return error;
    } finally {
      context.commit("setVerifyAuthenticatorLoading", false);
    }
  },
};

export default actions;
